/**
 * Presence Redux slice — 2026-05-03
 *
 * Holds the live presence map keyed by user id. Updated by:
 *   - REST hydrate (`bulkHydrate`) on app mount
 *   - Live WebSocket "presence" frames (`setPresence`)
 *
 * Status priority: on_leave > dnd/away > online > offline.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PresenceStatus = 'online' | 'away' | 'dnd' | 'offline' | 'on_leave';

export interface PresenceEntry {
  status: PresenceStatus;
  /** ISO date string (YYYY-MM-DD) — only meaningful when status === "on_leave" */
  leaveEndDate: string | null;
  /**
   * DND announcements carry their scheduled end time. Keeping it lets the
   * client reject a stale `away` frame from an older server until DND ends.
   */
  manualDndEndsAt: string | null;
  /** Local timestamp of the last update — useful for staleness checks */
  updatedAt: number;
}

export interface PresenceState {
  byUserId: Record<string, PresenceEntry>;
  /** True once the initial REST snapshot has been hydrated */
  hydrated: boolean;
}

const initialState: PresenceState = {
  byUserId: {},
  hydrated: false,
};

interface SetPresencePayload {
  userId: string;
  status: PresenceStatus;
  leaveEndDate?: string | null;
  manualDndEndsAt?: string | null;
}

interface BulkHydratePayload {
  rows: Array<{ user_id: string; status: PresenceStatus; leave_end_date: string | null }>;
  /**
   * True (default) when the snapshot is branch-complete (the WS
   * `presence_snapshot`), so users it omits can be safely pruned. Set false
   * for lower-confidence sources (e.g. the REST fallback, which may return a
   * filtered subset) — pruning then is skipped so real users don't vanish and
   * flicker offline.
   */
  full?: boolean;
}

const slice = createSlice({
  name: 'presence',
  initialState,
  reducers: {
    setPresence(state, action: PayloadAction<SetPresencePayload>) {
      const { userId, status, leaveEndDate, manualDndEndsAt } = action.payload;
      const existing = state.byUserId[userId];
      const existingDndEndsAt = Date.parse(existing?.manualDndEndsAt || '');
      const hasActiveManualDnd =
        existing?.status === 'dnd' &&
        Number.isFinite(existingDndEndsAt) &&
        existingDndEndsAt > Date.now();

      // Older API instances emit `away` for DND. The announcement message is
      // more specific, so keep DND until its stated end time instead of
      // immediately downgrading the chat-list badge.
      if (status === 'away' && hasActiveManualDnd) return;

      // PERF: identical status → leave the entry untouched. Rewriting it with
      // a fresh `updatedAt` produced a new state object on every replayed or
      // duplicated frame, which re-rendered every presence subscriber (each
      // conversation row, the chat page, the member panel) for no change.
      const nextLeaveEnd = leaveEndDate ?? null;
      const nextDndEnd =
        status === 'dnd' ? manualDndEndsAt ?? existing?.manualDndEndsAt ?? null : null;
      if (
        existing &&
        existing.status === status &&
        existing.leaveEndDate === nextLeaveEnd &&
        existing.manualDndEndsAt === nextDndEnd
      ) {
        return;
      }

      state.byUserId[userId] = {
        status,
        leaveEndDate: leaveEndDate ?? null,
        manualDndEndsAt:
          status === 'dnd'
            ? manualDndEndsAt ?? existing?.manualDndEndsAt ?? null
            : null,
        updatedAt: Date.now(),
      };
    },
    bulkHydrate(state, action: PayloadAction<BulkHydratePayload>) {
      // Merge — snapshot is authoritative *except* when the existing entry
      // was just updated by a live WS frame and the snapshot lags reality.
      // 5s window covers a request roundtrip; any older value loses to the
      // snapshot so legitimate transitions (online→away, online→offline,
      // away→online) propagate.
      const FRESH_WINDOW_MS = 5_000;
      const now = Date.now();
      const seen = new Set<string>();
      for (const r of action.payload.rows) {
        seen.add(r.user_id);
        const existing = state.byUserId[r.user_id];
        const existingDndEndsAt = Date.parse(existing?.manualDndEndsAt || '');
        const hasActiveManualDnd =
          existing?.status === 'dnd' &&
          Number.isFinite(existingDndEndsAt) &&
          existingDndEndsAt > now;
        const isFreshLive =
          !!existing &&
          existing.status !== 'offline' &&
          existing.status !== r.status &&
          (now - existing.updatedAt) < FRESH_WINDOW_MS;
        if (isFreshLive || (hasActiveManualDnd && r.status === 'away')) continue;
        // PERF: unchanged rows are skipped so a periodic snapshot that reports
        // the same presence does not rebuild the whole map (see setPresence).
        const nextLeaveEnd = r.leave_end_date ?? null;
        const nextDndEnd = r.status === 'dnd' ? existing?.manualDndEndsAt ?? null : null;
        if (
          existing &&
          existing.status === r.status &&
          existing.leaveEndDate === nextLeaveEnd &&
          existing.manualDndEndsAt === nextDndEnd
        ) {
          continue;
        }
        state.byUserId[r.user_id] = {
          status: r.status,
          leaveEndDate: r.leave_end_date ?? null,
          manualDndEndsAt:
            r.status === 'dnd' ? existing?.manualDndEndsAt ?? null : null,
          updatedAt: now,
        };
      }
      // Drop entries no longer present in the snapshot — they likely belong
      // to users who left the caller's branch. Only do this for a
      // branch-complete snapshot; a partial one must not evict real users.
      if (action.payload.full !== false) {
        for (const uid of Object.keys(state.byUserId)) {
          if (!seen.has(uid)) delete state.byUserId[uid];
        }
      }
      if (!state.hydrated) state.hydrated = true;
    },
    clearPresence(state) {
      state.byUserId = {};
      state.hydrated = false;
    },
  },
});

export const { setPresence, bulkHydrate, clearPresence } = slice.actions;

export default slice.reducer;

/* ------------------------------------------------------------------ */
/* Selectors                                                          */
/* ------------------------------------------------------------------ */

type RootSliceShape = { presence: PresenceState };

export const selectPresenceByUserId = (state: RootSliceShape, userId: string | null | undefined): PresenceEntry | undefined =>
  userId ? state.presence.byUserId[userId] : undefined;

export const selectPresenceStatus = (state: RootSliceShape, userId: string | null | undefined): PresenceStatus =>
  (userId && state.presence.byUserId[userId]?.status) || 'offline';

export const selectAnyMemberOnLeave = (state: RootSliceShape, memberIds: string[]): boolean =>
  memberIds.some((id) => state.presence.byUserId[id]?.status === 'on_leave');

export const selectMembersOnLeave = (state: RootSliceShape, memberIds: string[]): string[] =>
  memberIds.filter((id) => state.presence.byUserId[id]?.status === 'on_leave');

export const selectPresenceHydrated = (state: RootSliceShape): boolean =>
  state.presence.hydrated;
